import React, { useEffect, useState } from 'react';
import { UploadOutlined } from '@ant-design/icons';
import { Button, Upload, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import type { UploadRequestOption as RcCustomRequestOptions } from 'rc-upload/lib/interface';
import { v4 as uuidv4 } from 'uuid';

import Modal from 'src/components/custom/Modal';
import { usePriceQuotes } from 'src/queries/usePriceQuotes';
import { MACHINES_TO_SORT_SIZE, STATUS_CODE } from 'src/constants/app';
import {
  getSizes,
  removeZeroAndDuplicateItem,
  sortSizesByValue,
} from 'src/utils/helper';
import { I18n } from 'src/libs/i18n';
import { UseFormReturn } from 'react-hook-form';
import { FormValue } from '../useForm';

type Props = {
  formInfo: UseFormReturn<FormValue, any>;
  setDataReEstimate: (data: any) => void;
  setEstimateErrors: (e) => void;
};

const UploadCSVModal: React.FC<Props> = ({
  formInfo,
  setDataReEstimate,
  setEstimateErrors,
}) => {
  const [productEstimateDraft, setProductEstimateDraft] = useState<any[]>([]);

  const { t } = useTranslation();

  const { downloadFileCsvPriceQuotes, uploadFileCsvPriceQuotes } =
    usePriceQuotes({});

  const downloadCsv = () => {
    downloadFileCsvPriceQuotes.mutate();
  };

  const [fileName, setFileName] = useState<string | null>(null);
  const [messagesError, setMessagesError] = useState<string>('');
  const [isDisabledBtn, setIsDisabledBtn] = useState<boolean>(false);

  const customRequest = async (options: RcCustomRequestOptions) => {
    const { file } = options;
    setFileName((file as File).name);
    try {
      const response = await uploadFileCsvPriceQuotes.mutateAsync({
        file: file as File,
      });
      const resConvert = handleConvertSizes(response?.data?.quotations);
      setProductEstimateDraft(resConvert || []);
      const messages = generateUploadMessages(response?.data);
      setMessagesError(messages);
    } catch (error: any) {
      if (error.response && error.response.status === 400) {
        const { code } = error.response.data;
        setMessagesError(I18n.t(`message.${code}`));
      }
      setIsDisabledBtn(true);
    }
  };

  const handleConvertSizes = (quotations) => {
    const res = quotations.map((item) => {
      let sizes = getSizes(
        removeZeroAndDuplicateItem(item?.sizeRes?.sizes),
        true,
      );

      if (MACHINES_TO_SORT_SIZE.includes(item?.setdnKakoCd)) {
        sizes = sortSizesByValue(sizes);
      }
      return {
        ...item,
        id: uuidv4(),
        product: item?.productCodes,
        isCreate: false,
        sizeRes: {
          ...item?.sizeRes,
          sizes,
        },
      };
    });

    return res;
  };

  function generateUploadMessages(data): string {
    const { summarizedErrorCode, quotations } = data;

    if (!summarizedErrorCode || summarizedErrorCode === null) {
      setIsDisabledBtn(false);
      return t('message.W000003');
    }

    setIsDisabledBtn(summarizedErrorCode.startsWith('E'));

    // 1. If summarizedErrorCode = E0000105
    if (summarizedErrorCode === STATUS_CODE.MULTIPLE_ERROR) {
      return t('message.E000105');
    }

    // 2. If summarizedErrorCode = C000009
    if (summarizedErrorCode === STATUS_CODE.MULTIPLE_UPLOAD_WARNING) {
      return t('message.C000009');
    }

    // 3. summarizedErrorCode contains 'C' (e.g. 'C000005')
    if (summarizedErrorCode.startsWith('C')) {
      // Find rows that have msgCodes containing summarizedErrorCode
      const lines = quotations
        .filter(
          (q) =>
            Array.isArray(q.msgCodes) &&
            q.msgCodes.includes(summarizedErrorCode),
        )
        .map((q) => q.csvLineNo);

      if (lines.length > 0) {
        return `${t('multiple_quotes_page.line')} ${lines.join('、')} ${I18n.t(
          `message.${summarizedErrorCode}`,
        )}`;
      }
      // If not found then just return summarizedErrorCode
      return I18n.t(`message.${summarizedErrorCode}`);
    }

    // 4. summarizedErrorCode exists in msgCodes (other types like E000056)
    const errorLines = quotations
      .filter(
        (q) =>
          Array.isArray(q.msgCodes) && q.msgCodes.includes(summarizedErrorCode),
      )
      .map((q) => q.csvLineNo);

    if (errorLines.length > 0) {
      // If summarizedErrorCode = E0000030
      if (summarizedErrorCode.includes(STATUS_CODE.NON_EXISTENT_VALUE_ERROR)) {
        const isCommonShhnCd = quotations.some(
          (q) =>
            Array.isArray(q.msgCodes) &&
            q.msgCodes.includes(STATUS_CODE.NON_EXISTENT_VALUE_ERROR) &&
            q?.msgCodeDetails?.[STATUS_CODE.NON_EXISTENT_VALUE_ERROR] ===
              'E000030_common_shhn_cd',
        );

        const valueName = isCommonShhnCd
          ? t('quotation_page.processing')
          : t('multiple_quotes_page.processing_method');

        return `${t('multiple_quotes_page.line')} ${errorLines.join(
          '、',
        )} ${I18n.t(`message.E000030`, {
          0: valueName,
        })}`;
      }

      return `${t('multiple_quotes_page.line')} ${errorLines.join(
        '、',
      )} ${I18n.t(`message.${summarizedErrorCode}`)}`;
    }

    // 5. Other cases
    return t('message.W000003');
  }

  const handleAddDataByFileCSV = (onClose) => {
    const { customer_name, destination, destinationItem } =
      formInfo.getValues();
    formInfo.reset({
      customer_name,
      destination,
      destinationItem,
      priceQuotes: [],
    });
    setDataReEstimate(productEstimateDraft);
    setEstimateErrors([]);
    onClose();
  };

  const resetUpload = () => {
    setFileName(null);
    setMessagesError('');
  };

  useEffect(() => {
    if (uploadFileCsvPriceQuotes.isLoading) {
      setMessagesError('');
    }
  }, [uploadFileCsvPriceQuotes.isLoading]);

  return (
    <Modal
      modalProps={{
        width: 742,
        afterClose: () => {
          resetUpload();
        },
      }}
      title={t('multiple_quotes_page.upload_file')}
      content={({ onClose }) => (
        <div className="upload-csv-modal">
          <div className="upload-csv-modal__body">
            <Typography.Text
              className="dowload-file__btn"
              onClick={() => downloadCsv()}
            >
              {t('multiple_quotes_page.csv_template')}
            </Typography.Text>

            <div className="upload-file__wrap">
              <Upload
                accept=".csv"
                maxCount={1}
                customRequest={customRequest}
                showUploadList={false}
                key={Date.now()}
              >
                <Button
                  icon={<UploadOutlined />}
                  className="upload-file__btn"
                  loading={uploadFileCsvPriceQuotes.isLoading}
                >
                  {t('multiple_quotes_page.upload_file_btn')}
                </Button>
              </Upload>

              {fileName && (
                <Typography.Text className="file-name">
                  {fileName}
                </Typography.Text>
              )}
            </div>
            <div className="note-wrap">
              <Typography.Text className="note-content">
                {t('multiple_quotes_page.csv_upload_instructions')}
              </Typography.Text>
            </div>
            {messagesError && (
              <div className="errors-wrap">
                <Typography.Text className="errors-label">
                  {messagesError}
                </Typography.Text>{' '}
                <br />
              </div>
            )}
          </div>
          {fileName && !uploadFileCsvPriceQuotes.isLoading && (
            <div className="upload-csv-modal__footer">
              <Button
                type="primary"
                className="btn-submit"
                style={{ width: 170 }}
                disabled={isDisabledBtn}
                onClick={() => handleAddDataByFileCSV(onClose)}
              >
                {t('multiple_quotes_page.import')}
              </Button>
              <Button
                type="primary"
                className="btn-close"
                style={{ width: 170 }}
                onClick={onClose}
              >
                {t('multiple_quotes_page.button_cancel')}
              </Button>
            </div>
          )}
        </div>
      )}
    >
      <Button type="primary" className="btn-custom" style={{ width: 187 }}>
        {t('multiple_quotes_page.btn_csv')}
      </Button>
    </Modal>
  );
};

export default UploadCSVModal;
